"use client";

import { HiCheck, HiArrowRight } from "@/components/icons";
import BuyButton from "@/components/BuyButton";

interface BundleUpsellProps {
  source?: string;
  className?: string;
}

import { PRICING } from "@/lib/pricing-constants";
import {
  BUNDLE_FULL_VALUE,
  BUNDLE_PER_EXTENSION,
  BUNDLE_SAVINGS_PERCENT,
  PAID_PRODUCT_COUNT,
} from "@/lib/product-stats";

export default function BundleUpsell({ source = "products", className = "" }: BundleUpsellProps) {
  const maxWidthClass = source === "homepage" ? "max-w-6xl" : "max-w-5xl";
  return (
    <div id="bundle" className={`${maxWidthClass} mx-auto scroll-mt-32 ${className}`}>
      <div className="relative rounded-[2.5rem] border border-primary/20 bg-gradient-to-br from-primary/5 via-orange-500/5 to-yellow-500/5 p-8 md:p-12 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.05]"></div>
        {/* A "Limited Time Offer" ribbon used to sit here. Removed: the bundle
            price is a constant in lib/pricing-constants.ts with no end date and
            no scheduled change, so the claim was not true. Manufactured
            scarcity is also the same category of copy already scrubbed from
            this site — see the commits removing the fabricated testimonial
            claim and the self-serving review schema. The discount is real and
            large; it does not need a fake deadline. */}

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-3 py-1 mb-6 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase border border-primary/20">
              The Power User Bundle
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tighter">
              Get Everything. <br />
              <span className="text-primary">Forever.</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Unlock <strong>all {PAID_PRODUCT_COUNT} Pro extensions</strong> plus every future tool
              we build. One license key, unlimited usage, priority support.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <HiCheck />
                </div>
                <span className="font-medium">All Current & Future Extensions</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <HiCheck />
                </div>
                <span className="font-medium">Priority Support & Features</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <HiCheck />
                </div>
                <span className="font-medium">
                  Save {BUNDLE_SAVINGS_PERCENT}% vs Individual Lifetime Licenses
                </span>
              </div>
            </div>
          </div>

          <div className="bg-background/60 backdrop-blur-xl rounded-2xl p-8 border border-border/50 shadow-2xl">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-6xl font-bold tracking-tighter">
                  ${PRICING.BUNDLE_LIFETIME}
                </span>
                <div className="flex flex-col items-start">
                  <span className="text-xl text-muted-foreground line-through decoration-red-500/50 decoration-2">
                    ${BUNDLE_FULL_VALUE}
                  </span>
                  <span className="text-xs font-bold text-primary uppercase">One-time</span>
                </div>
              </div>
              <div className="inline-block bg-green-500/10 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm font-bold border border-green-500/20 mb-2">
                Save {BUNDLE_SAVINGS_PERCENT}%
              </div>
              <p className="text-sm text-muted-foreground">Lifetime access to everything</p>
            </div>

            <BuyButton
              product="bundle"
              className="w-full h-16 rounded-2xl bg-foreground text-background hover:bg-foreground/90 font-bold text-xl shadow-xl flex items-center justify-center gap-2 mb-4 transition-all hover:scale-[1.02]"
              source={source}
              source_id="bundle"
            >
              Get The Bundle <HiArrowRight />
            </BuyButton>

            {/* An unattributed quote — "The smartest investment I made for my
                workflow." — used to sit here. Removed: no name, no source, no
                way to stand behind it, and it is indistinguishable from an
                invented testimonial. Replaced with a fact that is checkable
                and does more work anyway: what the bundle costs per extension. */}
            <div className="bg-primary/5 rounded-lg p-4 text-center">
              <p className="text-xs font-medium text-muted-foreground">
                Works out to{" "}
                <strong className="text-foreground">${BUNDLE_PER_EXTENSION} per extension</strong> —
                paid once, yours for good.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
